import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CircleHelp, Lock, Search } from 'lucide-react';
import { notify } from '@/components/ui/notify';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { createPageUrl } from '@/utils';
import { AccountFinal } from '@/api/entities';
import { addMarketAccount } from '@/lib/marketExperienceApi';
import {
  formatPilotAllowanceCopy,
  isNotInPilotError,
  isPilotAtCap,
  isPilotModeOn,
  navigateToPilotLockedPage,
  PILOT_NO_SWAP_TOOLTIP,
  pilotErrorMessage,
  pilotLockChipLabel,
  pilotProgress,
} from '@/lib/pilotMode';
import {
  formatEnrolment,
  formatMomentum,
  formatFinancialCapacity,
  formatAiReadiness,
  getOpportunityTypeMeta,
  getIncumbent,
  rowCategories,
  COLUMN_TOOLTIPS,
  OPPORTUNITY_TYPE_META,
  opportunityTypeBadgeClass,
  resolveOpportunityType,
  isMarketAccountAdded,
  marketAccountPageId,
  extractAccountPageId,
  extractAddedAccount,
  normalizeMarketAccountCode,
  pickTenantMarketAccountRow,
  formatCategoryLabel,
  MAP_TYPE_FILTERS,
  marketAddSuccessTitle,
  queueIntro,
  mktLabel,
} from '@/lib/marketExperience';
import { useTranslation } from '@/components/utils/translations';
import MarketHelpLabel from './MarketHelpLabel';
import { cleanQuotedText } from '@/lib/quoteText';

function matchesAccountName(row, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return true;
  return String(row?.account_name || '').toLowerCase().includes(needle);
}

function TypeLegend() {
  const { t } = useTranslation();
  const types = Object.values(OPPORTUNITY_TYPE_META).filter((m) => !m.hideFromTable && m.key !== 'monitor');
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {t('type')}
          <CircleHelp className="w-3.5 h-3.5 text-[#5C6B7A]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 text-xs space-y-1.5" align="start">
        <p className="font-medium text-[#0A1A2F] mb-2">{mktLabel(t, COLUMN_TOOLTIPS.type)}</p>
        {types.map((m) => (
          <p key={m.key} className="flex items-center gap-2 text-[#5C6B7A]">
            <Badge className={`text-[10px] font-medium ${opportunityTypeBadgeClass(m.key)}`}>{mktLabel(t, m.label)}</Badge>
          </p>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function PilotProgressMarks({ filled, cap }) {
  if (!cap) return null;
  if (cap > 12) {
    const pct = Math.round((filled / cap) * 100);
    return (
      <span className="block w-16 h-1.5 rounded-full bg-gray-200 overflow-hidden" aria-hidden="true">
        <span className="block h-full bg-[#00C2C2]" style={{ width: `${pct}%` }} />
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5" aria-hidden="true">
      {Array.from({ length: cap }, (_, i) => (
        <span
          key={i}
          className={`h-2 w-2 rounded-[2px] ${i < filled ? 'bg-[#00C2C2]' : 'bg-gray-200'}`}
        />
      ))}
    </span>
  );
}

function PilotAllowanceChip({ status, atCap }) {
  const copy = formatPilotAllowanceCopy(status);
  if (!copy) return null;
  const { filled, cap } = pilotProgress(status);
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-base font-semibold ${
        atCap
          ? 'border-gray-300 bg-gray-50 text-[#0A1A2F]'
          : 'border-[#00C2C2]/40 bg-[#E6F8F8] text-[#0A1A2F]'
      }`}
    >
      <PilotProgressMarks filled={filled} cap={cap} />
      {copy}
    </span>
  );
}

function PilotLockChip({ status }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 h-7 px-2 text-[11px] font-medium rounded-md bg-gray-100 text-[#5C6B7A] border border-dashed border-gray-300 cursor-default">
          <Lock className="w-3 h-3" />
          {pilotLockChipLabel(status)}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left" className="max-w-xs">
        {PILOT_NO_SWAP_TOOLTIP}
      </TooltipContent>
    </Tooltip>
  );
}

function isPilotRowLocked({ pilotOn, atCap, added }) {
  return Boolean(pilotOn && atCap && !added);
}

function rowAction(row, {
  added,
  openingId,
  addingId,
  handleOpenAccount,
  handleAdd,
  locked,
  pilotOn,
  pilotStatus,
  t,
}) {
  if (locked) {
    return <PilotLockChip status={pilotStatus} />;
  }
  if (added) {
    return (
      <button
        type="button"
        className="inline-flex items-center h-7 px-2.5 text-xs font-medium rounded-md bg-[#E6F8F8] text-[#0A1A2F] border border-[#00C2C2]/30 hover:bg-[#D4F1F1] disabled:opacity-60"
        disabled={openingId === row.account_id}
        onClick={() => handleOpenAccount(row)}
      >
        {openingId === row.account_id
          ? t('mktOpening')
          : (pilotOn ? t('mktInPilot') : t('mktSelectedAccount'))}
      </button>
    );
  }
  return (
    <Button
      size="sm"
      className="bg-[#00C2C2] hover:bg-[#00A8A8] text-white h-7 px-2.5 text-xs"
      disabled={addingId === row.account_id}
      onClick={() => handleAdd(row)}
    >
      {addingId === row.account_id ? t('mktAdding') : (pilotOn ? t('mktAddToPilot') : t('mktAddToAccounts'))}
    </Button>
  );
}

export default function MarketOpportunitiesTable({
  rows,
  total,
  loading,
  offset,
  pageSize,
  onOffsetChange,
  onAccountClick,
  onAccountAdded,
  tenantId,
  lens,
  queue,
  typeFilter = 'all',
  onTypeFilterChange,
  onSearchActiveChange,
  pilotStatus = null,
  onPilotUsed,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [addingId, setAddingId] = useState(null);
  const [openingId, setOpeningId] = useState(null);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [recordIds, setRecordIds] = useState(() => ({}));
  const [searchByQueue, setSearchByQueue] = useState({});
  const searchKey = queue || 'all';
  const searchQuery = searchByQueue[searchKey] || '';
  const searching = Boolean(searchQuery.trim());
  const intro = queueIntro(queue);
  const pilotOn = isPilotModeOn(pilotStatus);
  const atCap = isPilotAtCap(pilotStatus);

  useEffect(() => {
    onSearchActiveChange?.(searching);
  }, [searching, onSearchActiveChange]);

  const visible = useMemo(() => {
    const base = rows || [];
    if (!searching) return base;
    return base.filter((r) => matchesAccountName(r, searchQuery));
  }, [rows, searching, searchQuery]);

  const filterAccounts = async (where) => {
    try {
      return await AccountFinal.filter(where, '', 5);
    } catch (e) {
      if (isNotInPilotError(e)) throw e;
      return [];
    }
  };

  const resolveRecordId = async (row) => {
    const stamped = recordIds[row.account_id] || marketAccountPageId(row);
    if (stamped) return stamped;
    if (!tenantId) return null;
    const code = normalizeMarketAccountCode(row.account_id, tenantId)
      || normalizeMarketAccountCode(row.tenant_account_id, tenantId);
    const byCode = code
      ? await filterAccounts({ tenant_id: tenantId, account_id: code })
      : [];
    const byName = row.account_name
      ? await filterAccounts({ tenant_id: tenantId, account_name: row.account_name })
      : [];
    const hit = pickTenantMarketAccountRow([...(byCode || []), ...(byName || [])], {
      tenantId,
      poolAccountId: row.account_id,
    });
    const id = hit ? extractAccountPageId(hit) : null;
    if (id && row.account_id) {
      setRecordIds((prev) => ({ ...prev, [row.account_id]: id }));
    }
    return id;
  };

  const handleOpenAccount = async (row) => {
    const key = row.account_id;
    setOpeningId(key);
    try {
      const id = await resolveRecordId(row);
      if (!id) {
        notify({
          title: t('mktAccountPageNotFound'),
          description: t('mktCouldNotResolveAccount'),
          variant: 'destructive',
        });
        return;
      }
      navigate(createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`));
    } catch (e) {
      if (isNotInPilotError(e)) {
        navigateToPilotLockedPage(navigate, createPageUrl, e.body || {});
        return;
      }
      notify({
        title: t('mktCouldNotOpenAccount'),
        description: e?.message || t('mktPleaseTryAgain'),
        variant: 'destructive',
      });
    } finally {
      setOpeningId(null);
    }
  };

  const handleAdd = async (row) => {
    const id = row.account_id;
    if (!id || !tenantId) return;
    setAddingId(id);
    // Flip the CTA immediately so the row does not keep showing Add while we resolve the UUID.
    setAddedIds((prev) => new Set(prev).add(id));
    onAccountAdded?.(id, { in_accounts: true });
    try {
      const result = await addMarketAccount(tenantId, { account_id: id }, lens);
      const added = extractAddedAccount(result);
      const recordId = extractAccountPageId(result, added, row);
      if (recordId) {
        setRecordIds((prev) => ({ ...prev, [id]: recordId }));
        onAccountAdded?.(id, { in_accounts: true, account_record_id: recordId });
      }
      notify({ title: mktLabel(t, marketAddSuccessTitle(result)), variant: 'success' });
      onPilotUsed?.();
    } catch (e) {
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      onAccountAdded?.(id, { in_accounts: false, already_added: false });
      notify({
        title: t('mktCouldNotAddAccount'),
        description: pilotErrorMessage(e, t('mktPleaseTryAgain')),
        variant: 'destructive',
      });
    } finally {
      setAddingId(null);
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
    <Card className="border-0 shadow-md">
      <CardContent className="p-4 md:p-6">
        <div className="mb-4 flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-[#0A1A2F]">{t('mktOpportunities')}</h2>
              {pilotOn ? <PilotAllowanceChip status={pilotStatus} atCap={atCap} /> : null}
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C6B7A] pointer-events-none" />
              <Input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  const next = e.target.value;
                  setSearchByQueue((prev) => ({ ...prev, [searchKey]: next }));
                }}
                placeholder={t('mktSearchAccounts')}
                aria-label={t('mktSearchAccountsByName')}
                className="h-8 pl-8 text-xs border-gray-200"
              />
            </div>
            {onTypeFilterChange ? (
              <Select value={typeFilter} onValueChange={onTypeFilterChange}>
                <SelectTrigger className="w-full sm:w-[160px] h-8 text-xs border-gray-200">
                  <SelectValue placeholder={t('type')} />
                </SelectTrigger>
                <SelectContent>
                  {MAP_TYPE_FILTERS.map((f) => (
                    <SelectItem key={f.key} value={f.key} className="text-xs">
                      {mktLabel(t, f.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : null}
          </div>
          </div>
          {intro ? (
            <p className="text-sm text-[#5C6B7A]">{mktLabel(t, intro)}</p>
          ) : null}
          <p className="text-xs text-[#5C6B7A]">
            {searching
              ? t('mktNMatchingQuery', { n: visible.length, query: searchQuery.trim() })
              : total != null
                ? t('mktRangeOfTotal', {
                  from: Math.min(offset + 1, total),
                  to: Math.min(offset + visible.length, total),
                  total,
                })
                : t('mktNAccounts', { n: visible.length })}
          </p>
        </div>

        {loading ? (
          <div className="h-64 bg-gray-100 rounded-md animate-pulse" />
        ) : visible.length === 0 ? (
          <p className="text-sm text-[#5C6B7A] py-8 text-center">
            {searching ? t('mktNoAccountsMatchQuery', { query: searchQuery.trim() }) : t('mktNothingInListYet')}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-[#5C6B7A]">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('account')}</th>
                  <th className="px-3 py-2 font-medium w-[10rem] min-w-[10rem]"><TypeLegend /></th>
                  <th className="px-3 py-2 font-medium">
                    <MarketHelpLabel label={t('mktScore')} hint={mktLabel(t, COLUMN_TOOLTIPS.score)} />
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <MarketHelpLabel
                      label={queue === 'adjacent' ? t('mktAdjacentPlayer') : t('mktIncumbent')}
                      hint={mktLabel(t, COLUMN_TOOLTIPS.incumbent)}
                    />
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <MarketHelpLabel label={t('mktFinancialCapacity')} hint={mktLabel(t, COLUMN_TOOLTIPS.financial)} />
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <MarketHelpLabel label={t('mktAiReadiness')} hint={mktLabel(t, COLUMN_TOOLTIPS.ai_readiness)} />
                  </th>
                  <th className="px-3 py-2 font-medium">
                    <MarketHelpLabel label={t('momentum')} hint={mktLabel(t, COLUMN_TOOLTIPS.momentum)} />
                  </th>
                  <th className="px-3 py-2 font-medium text-right"><span className="sr-only">{t('mktAdd')}</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const typeKey = resolveOpportunityType(row);
                  const meta = getOpportunityTypeMeta(typeKey);
                  const cats = rowCategories(row);
                  const incumbent = getIncumbent(row);
                  const added = addedIds.has(row.account_id) || isMarketAccountAdded(row);
                  const locked = isPilotRowLocked({ pilotOn, atCap, added });
                  return (
                    <tr
                      key={row.account_id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-3 py-2 align-top">
                        {locked ? (
                          <span className="font-medium text-[#0A1A2F]">{row.account_name}</span>
                        ) : (
                          <button
                            type="button"
                            className="font-medium text-[#00C2C2] hover:underline text-left"
                            onClick={() => onAccountClick?.(row)}
                          >
                            {row.account_name}
                          </button>
                        )}
                        <p className="text-[11px] text-[#5C6B7A]">
                          {row.country}
                          {row.enrolment != null ? ` · ${formatEnrolment(row.enrolment, row.enrolment_measure)}` : ''}
                        </p>
                        {row.why_now ? (
                          <p className="text-[11px] text-[#0A1A2F] mt-1 line-clamp-2" title={cleanQuotedText(row.why_now)}>
                            {cleanQuotedText(row.why_now)}
                          </p>
                        ) : null}
                        {cats.length > 1 && (
                          <Badge variant="outline" className="mt-1 text-[10px] font-normal text-[#5C6B7A]">
                            {t('mktNCategories', { n: cats.length })}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top w-[10rem] min-w-[10rem]">
                        <Badge className={`text-[11px] font-medium ${opportunityTypeBadgeClass(typeKey)}`}>
                          {mktLabel(t, meta.label)}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 align-top font-semibold text-[#0A1A2F]">
                        {row.market_score ?? '—'}
                      </td>
                      <td className="px-3 py-2 align-top text-[#0A1A2F]">
                        <div className="flex flex-col items-start gap-1">
                          <span>{incumbent.vendor || '—'}</span>
                          {incumbent.category ? (
                            <span className="text-[11px] text-[#5C6B7A]">
                              {formatCategoryLabel(incumbent.category)}
                            </span>
                          ) : null}
                          {incumbent.state_label ? (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-100 text-[#5C6B7A]">
                              {mktLabel(t, incumbent.state_label)}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-[#0A1A2F]">
                        {mktLabel(t, formatFinancialCapacity(row))}
                      </td>
                      <td className="px-3 py-2 align-top text-[#0A1A2F]">
                        {formatAiReadiness(row)}
                      </td>
                      <td className="px-3 py-2 align-top">{formatMomentum(row.momentum_4w)}</td>
                      <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                        {rowAction(row, {
                          added,
                          openingId,
                          addingId,
                          handleOpenAccount,
                          handleAdd,
                          locked,
                          pilotOn,
                          pilotStatus,
                          t,
                        })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!searching && total > pageSize && (
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              type="button"
              className="text-xs text-[#00C2C2] disabled:text-gray-300"
              disabled={offset <= 0 || loading}
              onClick={() => onOffsetChange(Math.max(0, offset - pageSize))}
            >
              {t('previous')}
            </button>
            <span className="text-xs text-[#5C6B7A]">
              {t('mktPageNOfM', { n: Math.floor(offset / pageSize) + 1, m: Math.max(1, Math.ceil(total / pageSize)) })}
            </span>
            <button
              type="button"
              className="text-xs text-[#00C2C2] disabled:text-gray-300"
              disabled={offset + pageSize >= total || loading}
              onClick={() => onOffsetChange(offset + pageSize)}
            >
              {t('next')}
            </button>
          </div>
        )}
      </CardContent>
    </Card>
    </TooltipProvider>
  );
}
